import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Phone, Globe } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n/LangContext";
import { T } from "@/i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES_EN = T.en.contact.types;

const inputStyle = {
  backgroundColor: "var(--bg-canvas)",
  borderColor: "var(--border-delicate)",
  color: "var(--text-primary)",
};

const INVITE_ICONS = [Globe, Mail, Phone];

export default function Contact({ theme }) {
  const { t } = useLang();
  const EMPTY = { fullName: "", email: "", phone: "", eventDate: "", address: "", inquiryType: TYPES_EN[0], location: "", message: "" };
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const invites = t("contact.invites");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, preferredTheme: theme });
      toast.success(t("contact.success"));
      setForm(EMPTY);
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-invite-section" className="px-5 py-24 sm:px-8 lg:py-36">
      <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <Eyebrow>{t("contact.eyebrow")}</Eyebrow>
          <h2 className="font-display mt-6 text-3xl font-light tracking-tight sm:text-4xl lg:text-5xl">
            {t("contact.h1")}
            <br />
            <span className="italic" style={{ color: "var(--accent-gold)" }}>{t("contact.h2")}</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {t("contact.sub")}
          </p>
          <div className="mt-10 space-y-6">
            {invites.map((it, i) => {
              const Icon = INVITE_ICONS[i];
              return (
                <div key={it.title} className="flex gap-4">
                  <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: "var(--pill-tag-bg)", color: "var(--pill-tag-text)" }}>
                    <Icon size={17} strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3 className="text-base font-medium">{it.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{it.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-10 font-mono-alt text-xs tracking-[0.15em]" style={{ color: "var(--text-muted)" }}>
            mahima.ca1@gmail.com · {t("contact.phoneLabel")}: +91 XXXXX XXXXX
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <form
            onSubmit={submit}
            className="rounded-3xl border p-7 sm:p-9"
            style={{ borderColor: "var(--border-delicate)", backgroundColor: "var(--bg-surface)" }}
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{t("contact.labels.name")}</label>
                <input data-testid="contact-input-name" required value={form.fullName} onChange={set("fullName")}
                  className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)]" style={inputStyle} placeholder={t("contact.ph.name")} />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{t("contact.labels.email")}</label>
                <input data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")}
                  className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)]" style={inputStyle} placeholder={t("contact.ph.email")} />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{t("contact.labels.phone")}</label>
                <input data-testid="contact-input-phone" required type="tel" value={form.phone} onChange={set("phone")}
                  className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)]" style={inputStyle} placeholder={t("contact.ph.phone")} />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{t("contact.labels.eventDate")}</label>
                <input data-testid="contact-input-eventdate" required type="date" value={form.eventDate} onChange={set("eventDate")}
                  className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)]" style={inputStyle} />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{t("contact.labels.type")}</label>
                <select data-testid="contact-select-type" value={form.inquiryType} onChange={set("inquiryType")}
                  className="w-full rounded-xl border px-4 py-3 text-sm outline-none" style={inputStyle}>
                  {TYPES_EN.map((val, i) => <option key={val} value={val}>{t(`contact.types.${i}`)}</option>)}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{t("contact.labels.location")}</label>
                <input data-testid="contact-input-location" value={form.location} onChange={set("location")}
                  className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)]" style={inputStyle} placeholder={t("contact.ph.location")} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{t("contact.labels.address")}</label>
                <input data-testid="contact-input-address" required value={form.address} onChange={set("address")}
                  className="w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)]" style={inputStyle} placeholder={t("contact.ph.address")} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{t("contact.labels.message")}</label>
                <textarea data-testid="contact-input-message" required rows={5} value={form.message} onChange={set("message")}
                  className="w-full resize-none rounded-xl border px-4 py-3 text-sm outline-none focus:border-[var(--border-hover)]" style={inputStyle}
                  placeholder={t("contact.ph.message")} />
              </div>
            </div>
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold tracking-wide transition-transform enabled:hover:scale-[1.02] disabled:opacity-60"
              style={{ backgroundColor: "var(--primary-btn-bg)", color: "var(--primary-btn-text)" }}
            >
              <Send size={15} />
              {sending ? t("contact.sending") : t("contact.submit")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
